/* eslint-disable */
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Appointment,
  AppointmentReasons,
  AppointmentStatus,
  UserBasic,
  userListRoleWise,
  userListWorkingHours,
} from '../../types';
import {
  appointmentReasons,
  getUsersByClinicAndTime,
  updateAppointment,
  userList,
} from '../../services/apiService';
import { convertDateToISOWithOffset, convertISOToTime, formatDateToISOWithOffsetFilter , formatTimeHHMM } from '../../services/time';
import { useAlert } from '../alertProvider';
import { RequiredLabel, timeDifference, toTitleCase } from '../../utils/commonUtils';
import { useClinic } from '../ClinicProvider';
import { useVerboseLog } from '../../utils/debug';

type IRescheduled = {
  data: Appointment | undefined;
  onClose: (value: boolean) => void;
  open?: boolean;
};

const Rescheduled: React.FC<IRescheduled> = ({ data, onClose, open }) => {
  const { addAlert } = useAlert();
  const clinicContext = useClinic();
  const verboseLog = useVerboseLog();
  const { t: tAppointmentForm } = useTranslation('appointment_form');
  const { t: tCalendar } = useTranslation('calendar');
  
  const [appointmentReasonsList, setAppointmentReasonsList] = useState<AppointmentReasons[]>([]);
  const [selectedPersonnel, setSelectedPersonnel] = useState<userListRoleWise | undefined>(undefined);
  const [userListData, setUserListData] = useState<userListWorkingHours[]>([]);
  const [isFieldDirty, setIsFieldDirty] = useState<boolean>(false);
  const [availableRoleList, setAvailableRoleList] = useState<UserBasic[]>([]);
  const [initialState, setInitialState] = useState<boolean>(true);
  // Localized department display
  const getDepartmentDisplayName = (departmentName: string) => {
    const nameMap: { [key: string]: string } = {
      'Admin': tCalendar('admin'),
      'Pathology': tCalendar('pathology'),
      'Semen Collection': tCalendar('semenCollection'),
      'Consultation': tCalendar('consultation'),
      'Ultrasound': tCalendar('ultrasound'),
      'Surgery': tCalendar('surgery')
    };
    return nameMap[departmentName] || departmentName;
  };


  const [formData, setFormData] = useState({
    selectedAppointmentReason: 0,
    reschedulingreason: '',
    remark: '',
    timefrom: data?.start_datetime,
    timeto: '',
    personnel: 0,
    date: '',
    patinet: data?.patient?.id,
    partner: data?.patient?.partner?.id,
  });

  const fetchAppointmentReasons = useCallback(()=>{
    appointmentReasons({ clinicId: Number(clinicContext.ClinicId) || data?.patient?.clinic, departmentId: Number(data?.department__id) })
      .then((data) => {
        setAppointmentReasonsList(data.objects);
      })
      .catch((error) => {
        verboseLog('Error fetching appointment reasons:', error);
      });
  },[clinicContext.ClinicId,data?.patient?.clinic,data?.department__id]);

  const getAvailableUsers = useCallback(() => {
    userList(10,`&clinic=${clinicContext.ClinicId ?? data?.patient?.clinic}&department=${data?.department__id}`)
      .then((data: { objects: userListWorkingHours[] }) => {
        setUserListData(data.objects);
      })
      .catch((error) => {
        verboseLog('Error fetching personnel by role:', error);
      });
  }, [
    formData.date,
    data?.appointment_date,
    data?.end_datetime,
    data?.department__id,
    data?.start_datetime,
  ]);

  const handleInputChange = (
    name: string,
    value: string | null | userListRoleWise
  ) => {
    setIsFieldDirty(false);
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]:
        name === 'personnel' && value
          ? (value as userListRoleWise).user
          : value,
    }));
  };

  const handleRescheduled = () => {
    const start = formData.timefrom ?? '';
    const end = formData.timeto || '';

    const timeDiff = timeDifference(start, end);

    const params = {
      user: selectedPersonnel?.id,
      appointment_date: formData.date,
      start_datetime: formData.timefrom,
      end_datetime: formData.timeto,
      appointment_reason: formData.selectedAppointmentReason,
      patient: data?.patient?.id,
      ...(data?.patient?.partner && { partner: data?.patient?.partner?.id }),
      remark: formData.remark,
      status: AppointmentStatus.SCHEDULED,
      cancel_or_reschedule_reason: formData.reschedulingreason,
    };

    if (!params.cancel_or_reschedule_reason.length) {
      addAlert(tAppointmentForm('kindlyFillRequiredFields'), 'error');
      setIsFieldDirty(true);
    } else if (timeDiff < 5 || timeDiff > 300) {
      addAlert(tAppointmentForm('timeSlotRangeError'), 'error');
    } else {
      updateAppointment(data?.id ?? 0, params)
        .then(() => {
          addAlert(tAppointmentForm('appointmentRescheduledSuccess'), 'success');
          onClose(false);
        })
        .catch((err) => {
          err?.response?.data?.errors?.map((err: { detail: string }) => {
            return addAlert(err.detail, 'error');
          });
        });
    }
  };
 
  const renderDetail = (
    label: string,
    value: string | undefined,
    isDisabled: boolean,
    type?: string,
    width?: number,
    rows?: number,
    isRequired: boolean = true,
    fieldKey?: string,
  ) => {
    const today = new Date().toISOString().split('T')[0];
    return (
      <Grid2
        size={{ xs: 12, sm: 6, md: width ?? 4 }}
        justifyContent="space-between"
      >
        <TextField
          fullWidth
          label={RequiredLabel(label, isRequired ?? true)}
          value={value}
          variant="outlined"
          type={type}
          multiline={!!rows}
          disabled={isDisabled}
          slotProps={{
            inputLabel: { sx: { color: '#333' } },
            htmlInput: { min: today },
          }}
          rows={rows}
          onChange={(e) => {
            const key = fieldKey ?? label.toLowerCase().replace(/ /g, '');
            handleInputChange(key, e.target.value);
          }}
          error={isFieldDirty && !value && isRequired}          
        />
      </Grid2>
    );
  };

  useEffect(() => {
    fetchAppointmentReasons();
  }, [fetchAppointmentReasons]);


  useEffect(() => {
    if (data) {
      const initialReason = appointmentReasonsList.find(
        (reason) => reason.reason === data.appointment_reason__reason
      );

      setFormData((prev) => ({
        ...prev,
        selectedAppointmentReason: initialReason?.id ?? 0,
        timefrom: formatTimeHHMM(new Date(data?.start_datetime || "")),
        timeto: formatTimeHHMM(new Date(data?.end_datetime || "")),
        personnel: data?.user?.id,
        date: data?.appointment_date,
      }));

      setSelectedPersonnel(data?.user);
    }
  }, [data, appointmentReasonsList]);

  useEffect(() => {
    getAvailableUsers();
  }, [getAvailableUsers]);

  function combineDateTime(obj: { date: string; timefrom: string }): string {
    const dateTimeString = `${obj.date}T${obj.timefrom}:00`; // Combine date and time
    const dateObj = new Date(dateTimeString); // Create a Date object
    
    return formatDateToISOWithOffsetFilter(dateObj);
  }

  const fetchPersonnelByRoleAndTime = useCallback(() => {
    
    const startDateTime = initialState ? convertDateToISOWithOffset(new Date(data?.start_datetime ?? '')) : combineDateTime({date:formData.date, timefrom: formData.timefrom ?? '' });
    const endDateTime = initialState ? convertDateToISOWithOffset(new Date(data?.end_datetime ?? '')) : combineDateTime({date:formData.date, timefrom: formData.timeto ?? '' });   
    
    setInitialState(false);

    getUsersByClinicAndTime(clinicContext.ClinicId, data?.department__id.toString() ?? null, startDateTime, endDateTime)
      .then((res: { objects: UserBasic[] }) => {
        const personnels = res.objects ?? [];
        setAvailableRoleList(personnels);
        verboseLog('Available personnel:', personnels);
      })
      .catch((error) => {
        verboseLog('Error fetching working hours:', error);
      });

  }, [data?.department__id, clinicContext.ClinicId, formData.date, formData.timefrom, formData.timeto, data?.start_datetime, data?.end_datetime]);                                       

  useEffect(() => {
    if (open) {
      fetchPersonnelByRoleAndTime();
    }
  }, [formData.date, formData.timefrom, formData.timeto, fetchPersonnelByRoleAndTime, open, data?.department__id, clinicContext.ClinicId]);

  useEffect(() => {    
    // Auto-update Time To when Time From or Reason changes
    const selectedReason = appointmentReasonsList.find(r => r.id === formData.selectedAppointmentReason);
    const duration = selectedReason?.default_duration || 15;
    // Validate timefrom format (HH:mm)
    const isValidTime = /^([01]\d|2[0-3]):([0-5]\d)$/.test(formData.timefrom || '');
    if (formData.timefrom && isValidTime) {
      const today = formData.date || new Date().toISOString().split('T')[0];
      const timeFromDate = new Date(`${today}T${formData.timefrom}:00`);
      if (!isNaN(timeFromDate.getTime())) {
        const timeToDate = new Date(timeFromDate.getTime() + duration * 60000);
        const timeToFormatted = timeToDate.toTimeString().slice(0, 5);
        setFormData(prev => ({ ...prev, timeto: timeToFormatted }));
      }
    }
  }, [formData.timefrom, formData.selectedAppointmentReason, formData.date, appointmentReasonsList]);

  return (
    <Box>
      <Divider
        style={{
          height: '1px',
          background: '#595959',
          marginTop: 20,
          marginBottom: 20,
        }}
      />

      <Grid2 size={{ md: 4 }} container spacing={3} mt={2}>
        {renderDetail(tAppointmentForm('firstNameLabel'), data?.patient?.first_name, true)}
        {renderDetail(tAppointmentForm('lastNameLabel'), data?.patient?.last_name, true)}
        {renderDetail(tAppointmentForm('sexAssignedAtBirthLabel'), toTitleCase(data?.patient?.gender ?? ''), true)}
        {renderDetail(tAppointmentForm('departmentLabel'), getDepartmentDisplayName(data?.department__name || ''), true)}
        {renderDetail(
          tAppointmentForm('previousTimeSlotLabel'),
          `${convertISOToTime(
            data?.start_datetime ?? '',
            true
          )} - ${convertISOToTime(data?.end_datetime ?? '', true)}`,
          true
        )}
        <Grid2 size={{ xs: 12, sm: 6, md: 4 }} justifyContent="space-between">
          <FormControl fullWidth size="small">
            <InputLabel id="appointment-reason-label">
              {RequiredLabel(tAppointmentForm('appointmentReasonLabel'), true)}
            </InputLabel>
            <Select
              labelId="appointment-reason-label"
              size="medium"
              sx={{ height: 55 }}
              value={formData.selectedAppointmentReason}
              onChange={(e) =>
                setFormData((prevFormData) => ({
                  ...prevFormData,
                  selectedAppointmentReason: Number(e.target.value),
                }))
              }
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: '300px',
                    width: '280px',
                  },
                },
              }}
              label={RequiredLabel(tAppointmentForm('appointmentReasonLabel'), true)}
            >
              {appointmentReasonsList?.map((reason) => (
                <MenuItem
                  key={reason.id}
                  value={reason.id}
                  style={{
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                  }}
                >
                  {reason.reason}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid2>

        {renderDetail(
          tAppointmentForm('reschedulingReasonLabel'),
          formData.reschedulingreason,
          false,
          '',
          12,
          undefined,
          true,
          'reschedulingreason'
        )}

        <Grid2 size={{ xs: 12, sm: 6, md: 4 }} justifyContent="space-between">
          <FormControl fullWidth size="small">
            <Autocomplete
              options={userListData}
              getOptionLabel={(option) =>
                `${option.first_name} ${option.last_name}`
              }
              value={selectedPersonnel || null}
              onChange={(_e, personnelNewValue: userListRoleWise | null) => {
                setSelectedPersonnel(personnelNewValue || undefined);
                handleInputChange('personnel', personnelNewValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={(() => {
                    if (data?.department__name === 'Admin') return tAppointmentForm('personnelLabel');
                    if (data?.department__name === 'Pathology') return tAppointmentForm('pathologistLabel');
                    return tAppointmentForm('doctorNameLabel');
                  })()}
                  variant="outlined"
                />
              )}
              renderOption={(props, option) => {
                const isAvailable = availableRoleList.some((user) => user.id === option.id);
                return (
                  <li {...props} key={option.id}>
                    <Typography
                      style={{
                        color: isAvailable ? 'green' : 'red',
                        fontWeight: isAvailable ? '700' : '600',
                      }}
                    >
                      {option.first_name} {option.last_name}
                    </Typography>
                  </li>
                );
              }}
            />
          </FormControl>
        </Grid2>
        {renderDetail(tAppointmentForm('dateLabel'), formData.date, false, 'date', undefined, undefined, true, 'date')}
        {renderDetail(tAppointmentForm('timeFromLabel'), formData.timefrom, false, 'time', undefined, undefined, true, 'timefrom')}
        {renderDetail(tAppointmentForm('timeToLabel'), formData.timeto, false, 'time', undefined, undefined, true, 'timeto')}
        {renderDetail(tAppointmentForm('remarkLabel'), formData.remark, false, '', 12, 3, false, 'remark')}
      </Grid2>

      <Stack direction="row" mt={3} gap={3}>
        <Button
          fullWidth
          sx={{
            color: '#232323',
            borderRadius: 3,
            borderColor: '#00000',
            borderWidth: 2,
            fontWeight: 700,
            fontFamily: 'Montserrat',
            fontSize: '16px',
          }}
          variant="outlined"
          onClick={() => onClose(false)}
        >
          {tAppointmentForm('keepItButton')}
        </Button>
        <Button
          fullWidth
          sx={{
            color: '#FFFFFF',
            borderRadius: 3,
            borderWidth: 2,
            fontWeight: 700,
            fontFamily: 'Montserrat',
            fontSize: '16px',
            background: '#000000',
          }}
          variant="outlined"
          onClick={handleRescheduled}
        >
          {tAppointmentForm('rescheduleAction')}
        </Button>
      </Stack>
    </Box>
  );
};

export default Rescheduled;
